import { useState } from 'react';
import { Box, IconButton, ListItemIcon, Menu, MenuItem, Divider  } from '@mui/material';
import { MoreHoriz } from '@mui/icons-material';
import { useTheme } from "@mui/material/styles";

export type MenuItemType =
	| {
			key: string;
			label: string;
			icon: JSX.Element;
			onClick: (event?: any) => void;
			disabled?: boolean;
			isExportButton?: undefined;
			isShowExportButton?: undefined;
	  }
	| {
			key: string;
			label: string;
			icon: JSX.Element;
			onClick: () => Promise<unknown>;
			disabled?: boolean;
			isExportButton: true;
			isShowExportButton: boolean;
	  };

type MoreOptionDropdownProps = {
	menuItems: MenuItemType[];
	isDisabled?: boolean;
	isShowDisabledButton?: boolean;
	iconColor?: string;
	icon?: React.ReactNode;
};

function ExportButton({
	onExport,
	disabled,
	children
}: {
	onExport: () => Promise<unknown>;
	disabled: boolean;
	children: string;
}) {
	const theme = useTheme();
	const colors =theme.palette;
	const [exporting, setExporting] = useState(false);
	return (
		<Box
			component="span"
			color={colors.table.text}
			sx={{
				pointerEvents: disabled || exporting ? 'none' : 'auto',
				cursor: disabled || exporting ? 'default' : 'pointer',
			}}
			onClick={async () => {
				setExporting(true);
				await onExport();
				setExporting(false);
			}}
		>
			{!exporting ? children : 'Exporting...'}
		</Box>
	);
}

const MoreOptionDropdown: React.FC<MoreOptionDropdownProps> = ({ menuItems, isDisabled = false, isShowDisabledButton=true, iconColor = 'table.moreIcon', icon }) => {
	const theme = useTheme();
	const colors =theme.palette;
	const [moreEl, setMoreEl] = useState<null | HTMLElement>(null);

	const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
		setMoreEl(event.currentTarget);
	};
	const handleMoreClose = () => {
		setMoreEl(null);
	};

	const hasVisibleExport = menuItems.some(
		(m) => m.isExportButton && m.isShowExportButton
	);

	let dividerRendered = false;

	return (
		<>
			<IconButton
				aria-label="more"
				id="more-button"
				aria-controls={!!moreEl ? 'more-menu' : undefined}
				aria-expanded={!!moreEl ? 'true' : undefined}
				aria-haspopup="true"
				onClick={handleMoreClick}
				sx={{
					padding: '4px 2px',
					borderRadius: 0,
					width: '42px',
					height: '45px',
					color: `${theme.palette.table.moreIcon} !important`,
					// display:
					//   tableType === "ML_ModelFeatureDrifts" ||
					//   tableType == "incident-details-ver" ||
					//   tableType == "add-schedules" ||
					//   tableType == "job-schedules-1"
					//     ? "none"
					//     : "block",
				}}
				disabled={isDisabled}
			>
				{icon || <MoreHoriz sx={{ color: iconColor }}/>}
			</IconButton>

			<Menu
				id="more-menu"
				MenuListProps={{ 'aria-labelledby': 'more-button' }}
				anchorEl={moreEl}
				open={!!moreEl}
				onClose={handleMoreClose}
				sx={{
					'&& .MuiPopover-paper, && .MuiMenu-paper': {
					backgroundColor: `${colors.widget.filter.bgColorPrimary} !important`,
					color: `${colors.table.text} !important`,
					minWidth: '160px',
					width: 'auto',
					}
				}}	
			>
					
				{menuItems.map(({ key, label, icon, onClick, disabled, isExportButton, isShowExportButton }) => {

					// If isShowDisabledButton is false, only show the Export button
					if (!isShowDisabledButton) {
						if (isExportButton && isShowExportButton) {
						  return (
							<MenuItem key={key} onClick={onClick}>
							  <ListItemIcon>{icon}</ListItemIcon>
							  <ExportButton onExport={onClick} disabled={!!disabled}>
								{label}
							  </ExportButton>
							</MenuItem>
						  );
						}
						// Skip rendering other buttons
						return null;
					  }

					  const shouldRender = ((isExportButton && isShowExportButton) || !isExportButton);
						if (!shouldRender) return null;

						const shouldShowDivider =
						hasVisibleExport && isExportButton && isShowExportButton && !dividerRendered;

						if (shouldShowDivider) dividerRendered = true;

					// If isShowDisabledButton is true, show all buttons (enabled/disabled)
					return (
							<Box key={key}>
								{shouldShowDivider && (
									<Divider sx={{ my: 0.5,  borderBottomWidth: 2, borderColor: 'rgba(0,0,0,0.12)' }} />
								)}
								<MenuItem key={key} disabled={!!disabled} {...(!isExportButton && { onClick })}>
									<ListItemIcon>{icon}</ListItemIcon>
									{isExportButton ? (
										<ExportButton onExport={onClick} disabled={!!disabled}>
											{label}
										</ExportButton>
									) : (
										<Box component="span" color={colors.table.text}>
											{label}
										</Box>
									)}
								</MenuItem>
							</Box>
						)
					})}
			</Menu>
		</>
	);
};

export default MoreOptionDropdown;
